/**
 * ProgressBar Component
 *
 * Visual indicator of progress or usage.
 * Used in subscription limits, upload progress, campaign completion, etc.
 *
 * Features:
 * - 4 color variants
 * - 3 sizes
 * - Optional label and percentage display
 * - Smooth animations
 * - Full dark mode support
 * - Accessible with ARIA attributes
 *
 * Usage:
 *   <ProgressBar
 *     value={75}
 *     max={100}
 *     label="Campaigns used"
 *     color="success"
 *   />
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface ProgressBarProps {
  /** Current value */
  value: number;
  /** Maximum value */
  max?: number;
  /** Optional label text */
  label?: string;
  /** Show percentage */
  showPercentage?: boolean;
  /** Color variant */
  color?: 'primary' | 'success' | 'warning' | 'error';
  /** Size of the progress bar */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showPercentage = true,
  color = 'primary',
  size = 'md',
  className
}) => {
  // Calculate percentage, ensuring it's between 0 and 100
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const colors = {
    primary: 'bg-primary',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error: 'bg-bad'
  };

  const sizes = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3'
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Label and percentage */}
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-1">
          {label && (
            <span className="text-sm font-medium text-text-primaryLight dark:text-text-primaryDark">
              {label}
            </span>
          )}
          {showPercentage && (
            <span className="text-sm text-text-secondaryLight dark:text-text-secondaryDark">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}

      {/* Progress bar track */}
      <div className={cn(
        'w-full bg-inset rounded-full overflow-hidden',
        sizes[size]
      )}>
        {/* Progress bar fill */}
        <div
          className={cn(
            'h-full transition-all duration-300 rounded-full',
            colors[color]
          )}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-label={label}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
